import React, { ReactNode } from 'react';

import { FlexRow } from '@editor/components/utilities/layouts';
import { useUserStore, userStoreSelector } from '@editor/stores/userStore';
import { getUserCreditsInfo } from '@editor/utils/credits';
import { SubscriptionPlan } from '@repo/domain/user';
import { BillingAdminAccess, useCurrentPlan } from '@repo/subscription/client';
import { H6, P3, P4 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import { Divider } from '../utilities/styles';

import { BuyCreditsButton, UpgradePlanButton } from './CreditButtons';
import {
  CreditsBalanceInfoContainer,
  CreditsBalanceInfoRow,
  CreditsBalanceInfoRowBox,
  CreditsBalanceInfoRowLeft,
  CreditsBalanceInfoWrapper,
} from './styles';

export enum UpgradeTrackingLabel {
  KittlAiMenu = 'upgradePlanWithMoreCreditsKittlAiMenu',
  Footer = 'upgradePlanWithMoreCreditsInfoFooter',
}

interface CreditsBalanceInfoProps {
  upgradeTrackingLabel?: UpgradeTrackingLabel;
}

const CreditsBalanceInfo: React.FC<CreditsBalanceInfoProps> = ({
  upgradeTrackingLabel,
}) => {
  const user = useUserStore(userStoreSelector.user);
  const currentPlan = useCurrentPlan();
  if (!user) {
    return null;
  }
  let yourPlan = '';
  if (!currentPlan) {
    yourPlan = 'Free';
  } else if (currentPlan === SubscriptionPlan.PRO) {
    yourPlan = 'Pro';
  } else if (currentPlan === SubscriptionPlan.EXPERT) {
    yourPlan = 'Expert';
  }

  const { extraCredits, remainingAmount, amount, formattedResetTime } =
    getUserCreditsInfo(user);

  const getBalanceRow = (
    label: string,
    value: string | number,
    dataTestId?: string,
    infoText?: string,
    infoTextDataTestId?: string
  ): ReactNode => {
    return (
      <CreditsBalanceInfoRowBox>
        <CreditsBalanceInfoRow>
          <CreditsBalanceInfoRowLeft>
            <H6>{label}</H6>
          </CreditsBalanceInfoRowLeft>
          <P3 data-testid={dataTestId}>{value}</P3>
        </CreditsBalanceInfoRow>
        {infoText && (
          <P4 color={themeStyle.varInkMedium} data-testid={infoTextDataTestId}>
            {infoText}
          </P4>
        )}
      </CreditsBalanceInfoRowBox>
    );
  };

  return (
    <CreditsBalanceInfoContainer>
      <CreditsBalanceInfoWrapper>
        {getBalanceRow('Your Plan', yourPlan, 'credits-balance-info-plan')}
        {!!currentPlan &&
          getBalanceRow(
            'Monthly Credits',
            `${remainingAmount}/${amount}`,
            'credits-balance-info-monthly',
            formattedResetTime,
            'credits-balance-info-reset-time'
          )}
        {extraCredits > 0 && (
          <>
            <Divider />
            {getBalanceRow(
              'Extra Credits',
              extraCredits,
              'credits-balance-info-extra'
            )}
          </>
        )}
      </CreditsBalanceInfoWrapper>
      <BillingAdminAccess>
        <FlexRow gap="8px">
          <BuyCreditsButton />
          <UpgradePlanButton
            fitSpace
            upgradeTrackingLabel={upgradeTrackingLabel}
          />
        </FlexRow>
      </BillingAdminAccess>
    </CreditsBalanceInfoContainer>
  );
};

export default CreditsBalanceInfo;
